import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

// Read-only rent fund summary. Shared by the staff page (/rent-fund) and the public
// investor link (/investor/:token), so both always show the same numbers.

const nf = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const n0 = (v) => nf.format(Math.round(Number(v) || 0));
const signed = (v) => `${v >= 0 ? "+" : "−"}${n0(Math.abs(v))}`;
const day = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const addDays = (iso, k) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + k); return d.toISOString().slice(0, 10); };

const STATUS = {
  // Status follows the rent fund (rent comes first; salaries and costs can slip a little).
  on_track: { label: "Rent on track", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  slightly_behind: { label: "Rent slightly behind", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  behind: { label: "Rent behind", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
  not_started: { label: "Plan starts soon", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  rent_secured: { label: "Rent secured", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  reached: { label: "Rent secured", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  missed: { label: "Rent short at deadline", cls: "bg-rose-500/15 text-rose-300 border-rose-500/30" },
};

export function RentFundStatus({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${s.cls}`} data-testid="rent-fund-status">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />{s.label}
    </span>
  );
}

function Stat({ label, value, sub, tone, testid, children }) {
  const color = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : "text-zinc-100";
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className={`font-display text-3xl font-extrabold mt-2 tabular-nums ${color}`}>{value}</div>
      {children}
      {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
    </div>
  );
}

export default function RentFundView({ data }) {
  const cur = data.facility?.currency_code || "";
  const money = (v) => `${cur} ${n0(v)}`;
  const { weeks, costs, phases } = data;
  const hasDue = data.due_to_date > 0;
  const pctCollected = Math.min(100, (data.collected / data.total_target) * 100);
  const pctDue = Math.min(100, (data.due_to_date / data.total_target) * 100);

  const chartData = weeks.map((w) => ({
    name: day(w.week_start),
    target: Math.round(w.target),
    actual: w.actual == null ? null : Math.round(w.actual),
    state: w.state,
  }));
  const barColor = (d) =>
    d.actual >= d.target ? "#34d399" : d.state === "current" ? "#60a5fa" : "#fb7185";

  let cumA = 0, cumT = 0, prev = null;
  const rows = weeks.map((w) => {
    cumT += w.target;
    const has = w.actual != null;
    if (has) cumA += w.actual;
    const wow = has && w.state !== "future" && prev != null && prev > 0 ? ((w.actual - prev) / prev) * 100 : null;
    // The week in progress is measured against plan up to yesterday, same as the headline figure.
    const cumGap = !has ? null : w.state === "current" ? data.gap : cumA - cumT;
    const row = { ...w, variance: has ? w.actual - w.target : null, wow, cumGap };
    if (has) prev = w.actual;
    return row;
  });
  const tone = (v) => (v == null ? "text-zinc-500" : v >= 0 ? "text-emerald-400" : "text-rose-400");

  const rentTotal = data.plan.rent;
  const hasRentFund = data.rent_set_aside != null;   // older payloads lack the rent-first fields
  const pctRent = rentTotal > 0 ? Math.min(100, (data.rent_set_aside / rentTotal) * 100) : 0;
  const pctRentDue = rentTotal > 0 ? Math.min(100, (data.rent_due_to_date / rentTotal) * 100) : 0;
  const rentAhead = data.rent_set_aside - data.rent_due_to_date;
  const bills = data.bills || [];
  const BILL_STATE = {
    paid: { label: "Paid", cls: "text-emerald-300" },
    overdue: { label: "Overdue", cls: "text-rose-300" },
    open: { label: "To pay", cls: "text-zinc-400" },
    unplanned: { label: "Not in plan", cls: "text-amber-300" },
  };

  return (
    <div className="space-y-6" data-testid="rent-fund-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {hasRentFund && (
          <Stat testid="rf-rent-fund" label="Rent fund" value={money(data.rent_set_aside)}
            sub={data.rent_left > 0
              ? `of ${money(rentTotal)} by ${day(data.plan.deadline)} · ${money(data.rent_weekly)} a week${data.rent_due_to_date > 0 ? ` · ${signed(rentAhead)} vs pace` : ""}`
              : `Full rent of ${money(rentTotal)} set aside`}>
            {data.rent_used_for_bills > 0 && (
              <div className="text-xs text-amber-300 mt-1.5" data-testid="rf-rent-dipped">
                {money(data.rent_used_for_bills)} of it went on bills paid ahead of plan
              </div>
            )}
            <div className="relative h-2 mt-3 rounded bg-[#141414] border border-[#262626] overflow-hidden"
              role="img" aria-label={`Rent set aside ${money(data.rent_set_aside)} of ${money(rentTotal)}`}>
              <div className="absolute inset-y-0 left-0 bg-emerald-500" style={{ width: `${pctRent}%` }} />
              {data.rent_due_to_date > 0 && <div className="absolute -inset-y-1 w-0.5 bg-zinc-100" style={{ left: `calc(${pctRentDue}% - 1px)` }} />}
            </div>
          </Stat>
        )}
        {hasRentFund && (
          <Stat testid="rf-rent-need" label="Needed per week for rent" value={money(data.rent_need_weekly)}
            sub={`${data.working_days_left} working days left to ${day(data.plan.deadline)}`} />
        )}
        {hasRentFund && data.salaries_paid != null ? (
          <Stat testid="rf-salaries" label="Money for salaries" value={money(data.salaries_paid)}
            sub={data.salaries_due_this_week > 0
              ? `of ${money(data.salaries_due_this_week)} due by this week's end${data.salaries_paid < data.salaries_due_this_week - 1 ? ` · ${money(data.salaries_due_this_week - data.salaries_paid)} short` : ""}`
              : "What's left each week after the rent fund's share, ads & utilities"} />
        ) : (
          <Stat testid="rf-gap" label="Ahead / behind plan" value={hasDue ? signed(data.gap) : "–"}
            tone={hasDue ? (data.gap >= 0 ? "pos" : "neg") : null}
            sub={hasDue ? `vs ${money(data.due_to_date)} planned up to yesterday` : "Starts after the first day"} />
        )}
        <Stat testid="rf-need" label="Needed per week for everything" value={money(data.need_weekly)}
          sub={`Rent, salaries, costs and pending, by ${day(data.need_by)}`} />
      </div>

      <section className="card-surface p-6 space-y-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h3 className="font-display text-xl font-extrabold tracking-tight">Progress to total target</h3>
          <span className="text-xs text-zinc-500">{day(data.plan.start)} – {day(data.plan.deadline)} · {data.total_days} days{data.working_days ? ` · ${data.working_days} working days (Mon–Sat)` : ""}</span>
        </div>
        <div className="relative h-4 rounded bg-[#141414] border border-[#262626] overflow-hidden"
          role="img" aria-label={`Collected ${money(data.collected)} of ${money(data.total_target)}; plan to date ${money(data.due_to_date)}`}>
          <div className="absolute inset-y-0 left-0 bg-blue-500 transition-all" style={{ width: `${pctCollected}%` }} />
          {hasDue && <div className="absolute -inset-y-1 w-0.5 bg-zinc-100" style={{ left: `calc(${pctDue}% - 1px)` }} />}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-500">
          <span>Collected <b className="text-zinc-200 tabular-nums">{money(data.collected)}</b></span>
          <span>Plan to date <b className="text-zinc-200 tabular-nums">{money(data.due_to_date)}</b> (white tick)</span>
          <span>Total target <b className="text-zinc-200 tabular-nums">{money(data.total_target)}</b></span>
        </div>
      </section>

      <section className="card-surface p-6 space-y-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h3 className="font-display text-xl font-extrabold tracking-tight">Week on week</h3>
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-[#2a2a2a]" />Weekly target</span>
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-400" />Hit</span>
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-rose-400" />Missed</span>
            <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-blue-400" />In progress</span>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={-28}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="name" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} width={44}
                tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
              <Tooltip cursor={{ fill: "#ffffff08" }}
                contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }}
                formatter={(v, k) => [money(v), k === "target" ? "Target" : "Revenue"]} />
              <Bar dataKey="target" fill="#2a2a2a" radius={[4, 4, 0, 0]} barSize={28} isAnimationActive={false} />
              <Bar dataKey="actual" radius={[4, 4, 0, 0]} barSize={16} isAnimationActive={false}>
                {chartData.map((d, i) => <Cell key={i} fill={barColor(d)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="card-surface p-6 space-y-3">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h3 className="font-display text-xl font-extrabold tracking-tight">Weekly ledger</h3>
          <span className="text-xs text-zinc-500">Net received from FPA sales, Monday to Sunday · {cur}</span>
        </div>
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full min-w-[960px] text-sm" data-testid="rent-fund-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#262626]">
                <th className="text-left font-medium py-2">Week</th>
                <th className="text-right font-medium py-2">Target</th>
                <th className="text-right font-medium py-2">Revenue</th>
                <th className="text-right font-medium py-2">Variance</th>
                <th className="text-right font-medium py-2">To rent fund</th>
                <th className="text-right font-medium py-2">Ads & utilities</th>
                <th className="text-right font-medium py-2">Salaries</th>
                <th className="text-right font-medium py-2">WoW</th>
                <th className="text-right font-medium py-2">Cumulative vs plan</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {rows.map((w) => (
                <tr key={w.week_start} className={`border-b border-[#1a1a1a] ${w.state === "current" ? "bg-blue-500/5" : ""} ${w.state === "future" ? "text-zinc-500" : ""}`}>
                  <td className="py-2.5 text-left">
                    <span className="font-medium">{day(w.week_start)} – {day(addDays(w.week_start, 6))}</span>
                    {w.state === "current" && <span className="ml-2 text-xs text-blue-400">this week</span>}
                    {w.state !== "current" && w.days < 7 && <span className="ml-2 text-xs text-zinc-500">{w.days} days</span>}
                  </td>
                  <td className="py-2.5 text-right">{n0(w.target)}</td>
                  <td className="py-2.5 text-right font-medium">{w.actual == null ? "–" : n0(w.actual)}</td>
                  <td className={`py-2.5 text-right ${w.state === "current" ? "text-zinc-500" : tone(w.variance)}`}>{w.variance == null ? "–" : signed(w.variance)}</td>
                  <td className="py-2.5 text-right" title={w.rent_target != null ? `Rent fund share this week: ${n0(w.rent_target)}` : undefined}>
                    {w.to_rent == null ? (w.rent_target != null ? <span className="text-zinc-600">{n0(w.rent_target)}</span> : "–") : n0(w.to_rent)}
                  </td>
                  <td className="py-2.5 text-right" title={w.fixed_target != null ? `Ads & utilities due this week: ${n0(w.fixed_target)}` : undefined}>
                    {w.to_fixed == null ? (w.fixed_target != null ? <span className="text-zinc-600">{n0(w.fixed_target)}</span> : "–") : n0(w.to_fixed)}
                  </td>
                  <td className={`py-2.5 text-right ${w.to_salaries != null && w.state === "past" && w.to_salaries < w.salaries_target - 1 ? "text-amber-300" : ""}`}
                    title={w.salaries_target != null ? `Salaries & pending due this week: ${n0(w.salaries_target)}` : undefined}>
                    {w.to_salaries == null ? (w.salaries_target != null ? <span className="text-zinc-600">{n0(w.salaries_target)}</span> : "–") : n0(w.to_salaries)}
                  </td>
                  <td className={`py-2.5 text-right ${tone(w.wow)}`}>{w.wow == null ? "–" : `${w.wow >= 0 ? "+" : "−"}${Math.abs(w.wow).toFixed(0)}%`}</td>
                  <td className={`py-2.5 text-right ${tone(w.cumGap)}`}>{w.cumGap == null ? "–" : signed(w.cumGap)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="pt-3 text-left">Total</td>
                <td className="pt-3 text-right tabular-nums">{n0(data.total_target)}</td>
                <td className="pt-3 text-right tabular-nums">{n0(data.collected)}</td>
                <td className="pt-3" />
                <td className="pt-3 text-right tabular-nums">{data.rent_set_aside != null ? n0(data.rent_set_aside) : ""}</td>
                <td className="pt-3 text-right tabular-nums">{data.fixed_paid != null ? n0(data.fixed_paid) : ""}</td>
                <td className="pt-3 text-right tabular-nums">{data.salaries_paid != null ? n0(data.salaries_paid) : ""}</td>
                <td colSpan={2} className="pt-3 text-right text-zinc-500 font-normal">{money(data.remaining)} still to collect</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {bills.length > 0 && (
        <section className="card-surface p-6 space-y-3" data-testid="rent-fund-bills">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h3 className="font-display text-xl font-extrabold tracking-tight">Bills paid</h3>
            <span className="text-xs text-zinc-500">
              From Expenses since {day(data.plan.start)} · {money(data.bills_paid_out)} paid out · {money(Math.max(0, data.cash_in_hand))} of collections left
            </span>
          </div>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[#262626]">
                  <th className="text-left font-medium py-2">Bill</th>
                  <th className="text-right font-medium py-2">Plan</th>
                  <th className="text-right font-medium py-2">Paid</th>
                  <th className="text-right font-medium py-2">Still to pay</th>
                  <th className="text-right font-medium py-2">Status</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {bills.map((b, i) => {
                  const st = BILL_STATE[b.state] || BILL_STATE.open;
                  return (
                    <tr key={i} className="border-b border-[#1a1a1a]">
                      <td className="py-2.5 text-left">
                        {b.label}
                        {b.due && b.state !== "paid" && !["ads", "utilities"].includes(b.kind) && b.label !== "Salaries (monthly)"
                          ? <span className="ml-2 text-xs text-zinc-500">by {day(b.due)}</span> : null}
                      </td>
                      <td className="py-2.5 text-right">{b.state === "unplanned" ? "–" : n0(b.planned)}</td>
                      <td className="py-2.5 text-right font-medium">{n0(b.paid)}</td>
                      <td className="py-2.5 text-right">{b.state === "unplanned" ? "–" : n0(b.left)}</td>
                      <td className={`py-2.5 text-right text-xs font-medium ${st.cls}`}>{st.label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-zinc-500">
            Everything logged in Expenses is money already paid out. Salary payments clear the oldest salary owed first,
            and rent payments clear pending rent before the rent itself. Once a month is over, ads and utilities follow what
            was really spent. Every bill comes out of the money collected, so if bills have taken more than their share,
            the rent fund shows less.
          </p>
        </section>
      )}

      <section className="card-surface p-6 space-y-4">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h3 className="font-display text-xl font-extrabold tracking-tight">What the target covers</h3>
          <span className="text-xs text-zinc-500">
            {phases.map((p) => `${money(p.weekly)}/week to ${day(p.to)}`).join(", then ")}
          </span>
        </div>
        <div className="divide-y divide-[#1f1f1f] text-sm">
          {costs.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-4 py-2.5">
              <span>{c.label}</span>
              <span className="text-xs text-zinc-500 self-center text-right">
                {c.kind === "rent" ? `one payment, due ${day(data.plan.deadline)}`
                  : c.kind === "pending" ? `pending, pay by ${day(c.due)}` : c.basis}
              </span>
              <span className="tabular-nums text-right w-24">{n0(c.amount)}</span>
            </div>
          ))}
          <div className="grid grid-cols-[1fr_auto] gap-4 py-2.5 font-semibold">
            <span>Total to collect</span>
            <span className="tabular-nums text-right">{money(data.total_target)}</span>
          </div>
        </div>
        <p className="text-xs text-zinc-500">
          The rent fund is built week by week and comes first. Each week's revenue goes to that week's rent share (plus any
          earlier shortfall), then ads & utilities (fixed), then salaries and pending payments; anything extra tops up the
          rent fund early. The status follows the rent fund. In a weak week salaries take the shortfall (amber in the ledger)
          and are caught up when revenue allows. Future weeks show the planned split in grey. Targets fall on working days only, Monday to Saturday; Sundays have no target, but Sunday sales still count.
          Rent is spread evenly across the working days to the deadline. Each pending payment is spread across the working
          days from the start date to its pay-by date. Monthly costs cover the whole period (× 12 ÷ 365 per calendar day) and are
          collected over its working days.
        </p>
      </section>
    </div>
  );
}
